import React, { useState } from 'react'
import classNames from 'classnames'
import { Flex } from '@chakra-ui/react'
import { Trans, t } from '@lingui/macro'

import {
  DashboardItemTypes,
  SearchableItemTypesEnum
} from 'front-end/src/graphql/private/PrivateSchemaTypes'

import { Modal } from '../../../components/common/modal/Modal'
import {
  ButtonPrimary,
  ButtonClean,
  Switch,
  FormControl,
  FormLabel
} from 'front-end/src/components'

import Field from '../../../components/common/form/field/Field'
import ResettableInput from '../../../components/common/form/input/ResettableInput'
import { Checkbox } from '../../../components/common/form/checkbox/Checkbox'
import { useDebouncedQuery } from '../../../app/hooks/useDebouncedQuery'
import Loader from '../../../components/common/loader/Loader'

import './AddItemModal.less'

import {
  QuickSearchQuery,
  QuickSearchQueryVariables,
  QuickSearchDocument
} from '../../../components/common/selectEntitiesForm/SelectEntitiesForm.codegen'

interface IProps {
  itemIds: number[]
  onSubmit: (selectedIds: number[], spreadItems: boolean) => Promise<void>
  onHide: () => void
  entityType: DashboardItemTypes
  title: React.ReactNode
}

export const AddItemModal = ({
  title,
  itemIds,
  entityType,
  onSubmit,
  onHide
}: IProps) => {
  const [query, setQuery] = useState('')
  const [selectedIds, setSelectedIds] = useState<number[]>(itemIds)
  const [addItems, setAddItems] = useState(false)

  const { data, loading } = useDebouncedQuery<
    QuickSearchQuery,
    QuickSearchQueryVariables
  >(
    {
      query: QuickSearchDocument,
      variables: {
        titleFilter: query,
        include: [SearchableItemTypesEnum[`${entityType}s`]]
      }
    },
    250
  )

  const handleSelectItem = (id: number): void => {
    const index = selectedIds.indexOf(id)
    const arrayCopy = [...selectedIds]
    if (index === -1) {
      // Not selected
      arrayCopy.push(id)
    } else {
      // Selected
      arrayCopy.splice(index, 1)
    }

    setSelectedIds([...arrayCopy])
  }

  return (
    <Modal
      show={true}
      onHide={onHide}
      header={<Trans>Add {title}</Trans>}
      subHeader={<Trans>Use data from {title} in this Dashboard</Trans>}
      controls={[
        <ButtonClean key={1} onClick={onHide}>
          <Trans>Cancel</Trans>
        </ButtonClean>,
        <ButtonPrimary
          key={2}
          className="Button__next"
          onClick={async () => {
            await onSubmit(selectedIds, addItems)
            onHide()
          }}
        >
          <Trans>Add</Trans> {title}
        </ButtonPrimary>
      ]}
    >
      <Field stretch>
        <ResettableInput
          placeholder={t`${title} name...`}
          value={query}
          onChange={(value: string): void => {
            setQuery(value)
          }}
          showResetButton={!!query}
          showSearchIcon={true}
          onReset={() => {
            setQuery('')
          }}
        />
      </Field>

      <div className="List">
        {loading && <Loader />}
        {!loading && data?.admin?.quickSearch?.length === 0 && (
          <p className="List__Row__noResults">
            <Trans>No results</Trans>
          </p>
        )}
        {!loading &&
          data?.admin?.quickSearch
            ?.filter((i) => !itemIds.includes(i.id))
            .map((searchableResource) => {
              const title =
                (searchableResource as any)?.currentTopicVersion?.title ||
                (searchableResource as any).title ||
                (searchableResource as any).eventTitle ||
                (searchableResource as any).name ||
                (searchableResource as any).campaignName

              return (
                <div
                  key={searchableResource.id}
                  className={classNames('List__Row', {
                    active: selectedIds.includes(searchableResource.id)
                  })}
                  onClick={(ev: React.MouseEvent<HTMLDivElement>) => {
                    ev.preventDefault()
                    handleSelectItem(searchableResource.id)
                  }}
                >
                  <Flex alignItems={'center'}>
                    <Checkbox
                      isChecked={selectedIds.includes(searchableResource.id)}
                      onChange={() => {
                        handleSelectItem(searchableResource.id)
                      }}
                    />
                    <div className="List__Row__firstCol">{title}</div>
                  </Flex>
                  {searchableResource.__typename === 'TopicModelAsMember' && (
                    <div className="List__Row__secondCol">
                      {searchableResource.workspace.name}
                    </div>
                  )}
                </div>
              )
            })}
      </div>
      {entityType === DashboardItemTypes.campaign && (
        <>
          <div className="AddItemModal__addItems">
            <FormControl
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <FormLabel fontSize="md" htmlFor="email-alerts" my="0">
                <Trans>Add items included in campaign</Trans>
              </FormLabel>
              <Switch
                isChecked={addItems}
                onChange={() => {
                  setAddItems(!addItems)
                }}
              />
            </FormControl>
          </div>
          <span className="AddItemModal__note">
            <Trans>
              Add also all items used in actions from the selected campaign,
              such as resources, pathways and surveys.
            </Trans>
          </span>
        </>
      )}
    </Modal>
  )
}
